import is from '@sindresorhus/is';
import type { ReactNode } from 'react';

import type { StructuredResponseEvent, StructuredToolId } from '../../data-contract/chat';
import { changeProposalRenderer } from './change-proposal';
import { clarifyOptionsRenderer } from './clarify-options';
import { helloHitlRenderer } from './hello-hitl';
import { reasoningRenderer } from './reasoning';
import type { InlineRenderContext, StructuredRenderer } from './types';

export type { InlineRenderContext, StructuredRenderer } from './types';

type StructuredRendererRegistry = {
    readonly [K in StructuredToolId]: StructuredRenderer<Extract<StructuredResponseEvent, { toolId: K }>>;
};

const RENDERERS = {
    [clarifyOptionsRenderer.toolId]: clarifyOptionsRenderer,
    [changeProposalRenderer.toolId]: changeProposalRenderer,
    [reasoningRenderer.toolId]: reasoningRenderer,
    [helloHitlRenderer.toolId]: helloHitlRenderer,
} as const satisfies StructuredRendererRegistry;

type RendererFor<TEvent extends StructuredResponseEvent> = StructuredRenderer<
    Extract<StructuredResponseEvent, { toolId: TEvent['toolId'] }>
>;

const findRendererFor = <TEvent extends StructuredResponseEvent>(event: TEvent): RendererFor<TEvent> =>
    RENDERERS[event.toolId] as RendererFor<TEvent>;

export const findInputOverrideNode = (structuredEvents: readonly StructuredResponseEvent[]): ReactNode | null => {
    for (const event of structuredEvents) {
        const node = findRendererFor(event).renderInputOverride?.(event);
        if (!is.nullOrUndefined(node)) return node;
    }
    return null;
};

export const renderInlineNode = (event: StructuredResponseEvent, context: InlineRenderContext): ReactNode | null =>
    findRendererFor(event).renderInline?.(event, context) ?? null;
